import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Turns a stored `url` (relative /api/files/...) into a fully-qualified URL
export function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("data:") || url.startsWith("http")) return url;
  if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
  return url;
}

export default function ImageUpload({ value, onChange, label = "Imagen", aspect = "aspect-video", testid = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const resolved = resolveImageUrl(value);

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("La imagen supera 10MB");
      return;
    }
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir la imagen");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <span className="text-xs tracking-widest uppercase text-inksoft">{label}</span>
      <div className={`mt-2 relative ${aspect} rounded-2xl overflow-hidden border-2 border-dashed border-warmgold/30 bg-sand/50 group`}>
        {resolved ? (
          <>
            <img src={resolved} alt={label} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/50 transition-colors flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
              <button onClick={handlePick} className="glass px-4 py-2 rounded-full text-sm flex items-center gap-2" data-testid={`${testid}-replace`}>
                <Upload size={14} /> Cambiar
              </button>
              <button onClick={() => onChange("")} className="glass px-4 py-2 rounded-full text-sm flex items-center gap-2" data-testid={`${testid}-remove`}>
                <X size={14} /> Quitar
              </button>
            </div>
          </>
        ) : (
          <button onClick={handlePick} disabled={uploading} className="absolute inset-0 w-full h-full flex flex-col items-center justify-center text-inksoft hover:bg-sand transition-colors" data-testid={`${testid}-picker`}>
            {uploading ? (
              <><Loader2 className="animate-spin text-warmgold" size={22} /><span className="text-xs mt-2">Subiendo...</span></>
            ) : (
              <><ImageIcon size={26} className="text-warmgold" /><span className="text-xs mt-2">Toca para subir imagen</span><span className="text-[10px] mt-1">JPG · PNG · WEBP · máx 10MB</span></>
            )}
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-input`} />
      </div>
      {resolved && (
        <p className="mt-1 text-[10px] text-inksoft break-all">
          <a href={resolved} target="_blank" rel="noreferrer" className="hover:text-warmgold">Ver original ↗</a>
        </p>
      )}
    </div>
  );
}
